import { useState } from 'react';
import { Plus } from 'lucide-react';
import { createPrompt } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const CATEGORIES = [
  { value: 'identity', label: 'Identity (身份)' },
  { value: 'personality', label: 'Personality (個性)' },
  { value: 'protocol', label: 'Protocol (協議)' },
  { value: 'tool', label: 'Tool (工具)' },
  { value: 'user', label: 'User (用戶)' },
  { value: 'custom', label: 'Custom (自訂)' },
] as const;

interface CreatePromptDialogProps {
  agentId: string;
  existingPromptNames: string[];
  onClose: () => void;
  onCreated: (prompt: { name: string }) => void;
}

export function CreatePromptDialog({
  agentId,
  existingPromptNames,
  onClose,
  onCreated,
}: CreatePromptDialogProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('custom');
  const [content, setContent] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateName = (value: string): string | null => {
    // Check length
    if (value.length < 3) {
      return '名稱至少需要 3 個字元';
    }
    if (value.length > 50) {
      return '名稱最多 50 個字元';
    }

    // Check format (alphanumeric, underscore, hyphen)
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
      return '名稱只能包含英文字母、數字、底線和連字符';
    }

    // Check for duplicates
    if (existingPromptNames.includes(value)) {
      return '此名稱已被使用';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate name
    const nameError = validateName(name);
    if (nameError) {
      setError(nameError);
      return;
    }

    setIsCreating(true);
    try {
      await createPrompt(agentId, {
        name,
        content: content || `# ${name}\n\nInitial prompt content.`,
        category,
      });

      onCreated({ name });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '建立 Prompt 失敗');
    } finally {
      setIsCreating(false);
    }
  };

  const nameError = name ? validateName(name) : null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            建立新 Prompt
          </DialogTitle>
          <DialogDescription>
            建立一個新的 Prompt 模板。名稱必須唯一且只能包含英文字母、數字、底線和連字符。
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Name Input */}
          <div className="space-y-2">
            <Label htmlFor="prompt-name">
              Prompt 名稱 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="prompt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my_prompt"
              className={nameError ? 'border-destructive' : ''}
            />
            {nameError && (
              <p className="text-xs text-destructive">{nameError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              3-50 字元，僅限英數字、底線、連字符
            </p>
          </div>

          {/* Category Select */}
          <div className="space-y-2">
            <Label htmlFor="prompt-category">分類</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="prompt-category">
                <SelectValue placeholder="選擇分類" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Initial Content (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="prompt-content">初始內容 (選填)</Label>
            <textarea
              id="prompt-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="可選：輸入初始內容，之後可在編輯器修改"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
            />
          </div>

          {/* Error Message */}
          {error && !nameError && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              取消
            </Button>
            <Button type="submit" disabled={isCreating || !name || !!nameError}>
              {isCreating ? '建立中...' : '建立'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
